import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";

/**
 * Renders restaurant menu HTML scaled to fit the player viewport (no scroll).
 */
export default function RestaurantWidgetMenu({ html, active }) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  const fitToViewport = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) {
      return;
    }

    const availableH = container.clientHeight;
    const availableW = container.clientWidth;
    const contentH = content.scrollHeight;
    const contentW = content.scrollWidth;

    if (!availableH || !contentH) {
      setScale(1);
      return;
    }

    const next = Math.min(
      1,
      availableH / contentH,
      availableW / Math.max(contentW, 1)
    );
    setScale(next);
  }, []);

  useLayoutEffect(() => {
    if (!active) {
      return undefined;
    }

    const runFit = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(fitToViewport);
      });
    };
    runFit();

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const observer = new ResizeObserver(() => runFit());
    observer.observe(container);

    const content = contentRef.current;
    if (content) {
      observer.observe(content);
    }

    const onLoad = () => fitToViewport();
    window.addEventListener("resize", onLoad);
    window.addEventListener("orientationchange", onLoad);

    const images = container.querySelectorAll("img");
    images.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", onLoad);
      }
    });

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onLoad);
      window.removeEventListener("orientationchange", onLoad);
      images.forEach((img) => img.removeEventListener("load", onLoad));
    };
  }, [html, active, fitToViewport]);

  return (
    <div ref={containerRef} className="restaurant-widget-menu">
      <div
        ref={contentRef}
        className="restaurant-widget-fit-host"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(html || ""),
        }}
      />
    </div>
  );
}
